import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { ChatThread, ChatMessage } from '../features/chat/chat.types';
import { useAuth } from '../features/auth/AuthContext';
import * as historyService from '../services/historyService';
import { stripAreaContext } from '../utils/areaContext';

interface ConversationContextValue {
  // Most recently accessed first: ordered by updatedAt, which is when the user
  // last opened or added to each conversation (the AMP API's lastAccessed for
  // fetched rows, bumped locally on open and on send).
  threads: ChatThread[];
  // Total conversations available server-side (from the initial 10-item page)
  // — lets HistoryModal know whether there's more to page in without an
  // extra round trip just to find out.
  historyTotalCount: number;
  getThread: (id: string) => ChatThread | undefined;
  createThread: (firstMessage: ChatMessage, conversationId?: string) => string;
  addMessageToThread: (threadId: string, message: ChatMessage) => void;
  setThreadAgent: (threadId: string, agentId: string, agentName: string) => void;
  loadConversation: (id: string, forceRefresh?: boolean) => Promise<ChatThread | undefined>;
  /** The user opened an already-loaded thread (no history fetch, so nothing stamped it
   *  server-side): moves it to the top here and records the access with the API. */
  touchThread: (id: string) => void;
  /** One-way archive: removes the chat from history (optimistically) and calls the API.
   *  Restores it and rethrows (user-facing message) if the API call fails. */
  archiveThread: (id: string) => Promise<void>;
  historyLoading: boolean;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const useConversations = (): ConversationContextValue => {
  const ctx = useContext(ConversationContext);
  if (!ctx) throw new Error('useConversations must be used within ConversationProvider');
  return ctx;
};

function generateId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function deriveTitle(raw: string): string {
  const content = stripAreaContext(raw);
  const maxLen = 40;
  if (content.length <= maxLen) return content;
  return content.substring(0, maxLen).trimEnd() + '...';
}

export const ConversationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const hasFetched = useRef(false);

  // Fetch the first page (10 most recent) from API when user is authenticated.
  // Browsing further pages / searching is handled locally by HistoryModal —
  // this context only ever holds the first page plus anything created client-side.
  useEffect(() => {
    if (!isAuthenticated) {
      hasFetched.current = false;
      return;
    }
    if (hasFetched.current) return;
    hasFetched.current = true;

    setHistoryLoading(true);
    historyService.fetchRecentHistoryForAmp(undefined, undefined, undefined, 10, 0, undefined, 'lastAccessed')
      .then(({ threads: apiThreads, totalCount }) => {
        setHistoryTotalCount(totalCount);
        setThreads((prev) => {
          // Merge: keep local threads that aren't in API, prepend API threads
          const apiIds = new Set(apiThreads.map((t) => t.id));
          const localOnly = prev.filter((t) => !apiIds.has(t.id));
          return [...apiThreads, ...localOnly];
        });
      })
      .finally(() => setHistoryLoading(false));
  }, [isAuthenticated]);

  const getThread = useCallback(
    (id: string): ChatThread | undefined => threads.find((t) => t.id === id),
    [threads]
  );

  const createThread = useCallback((firstMessage: ChatMessage, conversationId?: string): string => {
    const id = conversationId || generateId();
    const now = new Date().toISOString();
    const newThread: ChatThread = {
      id,
      title: deriveTitle(firstMessage.content),
      messages: [firstMessage],
      createdAt: now,
      updatedAt: now,
    };
    setThreads((prev) => [newThread, ...prev]);
    return id;
  }, []);

  const addMessageToThread = useCallback((threadId: string, message: ChatMessage) => {
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId
          ? { ...t, messages: [...t.messages, message], updatedAt: new Date().toISOString() }
          : t
      )
    );
  }, []);

  // Tags a thread with the Digital Workforce agent that's handling it — called
  // whenever a message is sent while a dwAgent is assigned, so reopening the
  // same conversation later (e.g. from the sidebar's Chat History) re-assigns
  // the same agent instead of falling back to the default AMS copilot.
  const setThreadAgent = useCallback((threadId: string, agentId: string, agentName: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === threadId ? { ...t, agentId, agentName } : t))
    );
  }, []);

  // Load a conversation from API (for history items that haven't been loaded yet)
  const loadConversation = useCallback(async (id: string, forceRefresh = false): Promise<ChatThread | undefined> => {
    // Check if already loaded with messages
    const existing = threads.find((t) => t.id === id);
    if (!forceRefresh && existing && existing.messages.length > 0) return existing;

    const { messages, isVoice } = await historyService.fetchConversationDetail(id);
    if (messages.length === 0) return undefined;

    // Loading is opening: the fetch above stamped the conversation as accessed
    // server-side, and updatedAt (which orders `threads`) follows it here.
    const thread: ChatThread = {
      id,
      title: existing?.title || deriveTitle(messages[0].content),
      messages,
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agentId: existing?.agentId,
      agentName: existing?.agentName,
      isVoice: isVoice || existing?.isVoice,
    };

    setThreads((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx >= 0) {
        // Update existing stub with full messages
        const updated = [...prev];
        updated[idx] = thread;
        return updated;
      }
      return [thread, ...prev];
    });

    return thread;
  }, [threads]);

  const touchThread = useCallback((id: string) => {
    let known = false;
    setThreads((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1) return prev;
      known = true;
      const updated = [...prev];
      updated[idx] = { ...prev[idx], updatedAt: new Date().toISOString() };
      return updated;
    });
    if (known) void historyService.touchConversation(id);
  }, []);

  const archiveThread = useCallback(async (id: string) => {
    let removed: { thread: ChatThread; index: number } | null = null;
    setThreads((prev) => {
      const index = prev.findIndex((t) => t.id === id);
      if (index === -1) return prev;
      removed = { thread: prev[index], index };
      return prev.filter((t) => t.id !== id);
    });
    setHistoryTotalCount((n) => Math.max(0, n - 1));
    try {
      await historyService.archiveConversation(id);
    } catch (err) {
      const restore = removed as { thread: ChatThread; index: number } | null;
      if (restore) {
        setThreads((prev) => {
          if (prev.some((t) => t.id === id)) return prev;
          const next = [...prev];
          next.splice(Math.min(restore.index, next.length), 0, restore.thread);
          return next;
        });
      }
      setHistoryTotalCount((n) => n + 1);
      throw err;
    }
  }, []);

  // Most recently accessed first (see ConversationContextValue.threads). Local
  // bumps — a send, an open — reorder here without the API round trip; a
  // thread with no usable updatedAt sinks to the bottom rather than crashing.
  const orderedThreads = useMemo(() => {
    const time = (t: ChatThread) => {
      const ms = new Date(t.updatedAt).getTime();
      return Number.isNaN(ms) ? 0 : ms;
    };
    return [...threads].sort((a, b) => time(b) - time(a));
  }, [threads]);

  const value: ConversationContextValue = {
    threads: orderedThreads,
    historyTotalCount,
    getThread,
    createThread,
    addMessageToThread,
    setThreadAgent,
    loadConversation,
    touchThread,
    archiveThread,
    historyLoading,
  };

  return (
    <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>
  );
};
